import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { resolveVocab } from "@/util/vocab";

/**
 * React equivalent of the ASP `Vocab(label)` helper. Returns a function that
 * swaps a default label for the site's Vocabulary replacement, if one exists.
 *
 * @returns {(label: any, fieldName?: string) => any}
 */
export const useVocab = () => {
  const map = useSelector((state) => state?.vocabulary?.map);

  return useCallback(
    (label, fieldName) => resolveVocab(map, label, fieldName),
    [map]
  );
};

/**
 * A form label renamed to blank still needs its row, or the field shifts up
 * out of line with its neighbours, so it is shown as a non-breaking space.
 * A label that was blank to begin with (label="") is left as is.
 *
 * @param {any} label     - label before renames
 * @param {any} shown     - label after renames
 */
export const keepBlankLabelSpace = (label, shown) =>
  shown === "" && typeof label === "string" && label !== "" ? " " : shown;

/**
 * Applies the site's label renames to grid column headings
 * ([{ key, text }], as used by useGridColumnConfig/DataTable), matching each
 * on its text, then its key. Apply it to the headings being rendered, not to
 * the defaults passed into useGridColumnConfig, so renames that arrive after
 * the grid mounts still show.
 *
 * @param {Array<{key: string, text: string}>} headers
 */
export const useVocabHeaders = (headers) => {
  const vocab = useVocab();

  return useMemo(
    () =>
      headers?.map((header) =>
        typeof header?.text === "string"
          ? { ...header, text: vocab(header.text, header.key) }
          : header
      ),
    [headers, vocab]
  );
};
